import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';
import LanguageToggle from '../LanguageToggle';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  const navLinks = [
    { id: 1, labelKey: 'header.home', href: '/' },
    { id: 2, labelKey: 'header.about', href: '/hakkimizda' },
    { id: 3, labelKey: 'header.purpose', href: '/amac' },
    { id: 4, labelKey: 'header.trainings', href: '/egitimler' },
    { id: 5, labelKey: 'header.projects', href: '/projeler' },
    { id: 6, labelKey: 'header.events', href: '/etkinlikler' },
    { id: 7, labelKey: 'header.opportunities', href: '/firsatlar' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(href);
      window.scrollTo(0, 0);
    }
    setIsMobileMenuOpen(false);
  };

  const handleContactClick = () => {
    if (location.pathname === '/') {
      const element = document.querySelector('#contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/#contact');
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-[#060b1a]/95 backdrop-blur-md border-b border-white/10'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => handleNavClick('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_cyberyouth/artifacts/z6i2gnpf_1-Photoroom.png" 
              alt="Cyber Logo" 
              className="h-10 w-auto"
            />
            <span className="text-white font-semibold text-lg hidden sm:block">Cyber</span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => handleNavClick(link.href)}
                className={`text-sm font-medium transition-colors duration-300 ${
                  location.pathname === link.href
                    ? 'text-[#4DFF88]'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {t(link.labelKey)}
              </button>
            ))}
          </nav>

          {/* Right side - Language Toggle + CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <LanguageToggle />
            <Button
              onClick={handleContactClick}
              className="bg-[#4DFF88] hover:bg-[#4DFF88]/10 hover:text-[#4DFF88] text-[#060b1a] font-medium px-6 py-2 rounded-none transition-all duration-300"
            >
              {t('header.contact')}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-3">
            <LanguageToggle />
            <button
              className="text-white p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-[#060b1a]/98 backdrop-blur-lg border-t border-white/10">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => handleNavClick(link.href)}
                className={`block w-full text-left py-2 text-base font-medium transition-colors ${
                  location.pathname === link.href
                    ? 'text-[#4DFF88]'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {t(link.labelKey)}
              </button>
            ))}
            <Button
              onClick={handleContactClick}
              className="w-full bg-[#4DFF88] hover:bg-[#4DFF88]/10 hover:text-[#4DFF88] text-[#060b1a] font-medium py-3 rounded-none mt-4"
            >
              {t('header.contact')}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
